import { useState, useEffect } from 'react'
import { useAuth } from '../auth/AuthContext'
import { useLanguage } from '../shared/LanguageContext'
import PortalHeader from '../shared/PortalHeader'
import { fetchAll, submitHomework, fetchStudentIdentity } from '../shared/api'
import {
  ScheduleSection,
  HomeworkSection,
  ProgressSection,
  GoalsSection,
} from '../parent/ParentLayout'

export default function StudentLayout() {
  const { studentIds, portalToken } = useAuth()
  // `language` drives the localized weekday in schedule secondary lines
  // (EN `Thu` / CN `周四`); without it every student sees English weekdays.
  const { t, language } = useLanguage()
  const studentId = studentIds[0] || null
  const [studentName, setStudentName] = useState('')
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)

  // Fetch student name
  useEffect(() => {
    if (!studentId || !portalToken) return
    let cancelled = false

    async function loadName() {
      const result = await fetchStudentIdentity(studentId, portalToken)
      if (!cancelled && result?.data?.name) {
        setStudentName(result.data.name)
      }
    }

    loadName()
    return () => { cancelled = true }
  }, [studentId, portalToken])

  // Load ALL data in one shot
  useEffect(() => {
    if (!studentId || !portalToken) return
    let cancelled = false
    setLoading(true)
    fetchAll(studentId, portalToken).then(result => {
      if (cancelled) return
      setData(result)
      setLoading(false)
    })
    return () => { cancelled = true }
  }, [studentId, portalToken])

  // Reload after adding a goal or uploading homework
  function reloadData() {
    if (!studentId || !portalToken) return
    fetchAll(studentId, portalToken).then(result => {
      setData(result)
    })
  }

  return (
    <div className="portal-shell">
      <PortalHeader studentName={studentName} freshness={data?.sync} />

      {loading ? (
        <div className="page-loading"><div className="spinner" /></div>
      ) : (
        <div className="portal-content portal-content--single">
          <ScheduleSection schedule={data?.schedule} progress={data?.progress} homework={data?.homework} t={t} language={language} />
          <HomeworkSection
            homework={data?.homework}
            progress={data?.progress}
            t={t}
            renderAssignmentAction={({ assignment, status }) => (
              (status === 'not_submitted' || status === 'returned') && (
                <UploadWidget
                  assignmentId={assignment.id}
                  studentId={studentId}
                  portalToken={portalToken}
                  onUploaded={reloadData}
                  t={t}
                />
              )
            )}
          />
          <ProgressSection progress={data?.progress} schedule={data?.schedule} t={t} />
          <GoalsSection
            goals={data?.goals}
            studentId={studentId}
            portalToken={portalToken}
            t={t}
            onGoalAdded={reloadData}
            setBy="student"
          />
        </div>
      )}
    </div>
  )
}


/* ─── Upload Widget ───────────────────────────────────────── */

function UploadWidget({ assignmentId, studentId, portalToken, onUploaded, t }) {
  const [file, setFile] = useState(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState(null)
  const [uploadSuccess, setUploadSuccess] = useState(false)

  async function handleUpload() {
    if (!file) return
    setUploading(true)
    setUploadError(null)
    setUploadSuccess(false)
    try {
      await submitHomework(assignmentId, studentId, file, portalToken)
      setUploadSuccess(true)
      setFile(null)
      if (onUploaded) onUploaded()
    } catch (err) {
      setUploadError(err.message)
    }
    setUploading(false)
  }

  return (
    <div className="upload-area" style={{ marginTop: '8px', borderTop: '1px solid var(--border)', paddingTop: '8px' }}>
      <input
        type="file"
        onChange={e => setFile(e.target.files[0] || null)}
        disabled={uploading}
        style={{ fontSize: '12px' }}
      />
      <button
        className="upload-btn"
        onClick={handleUpload}
        disabled={!file || uploading}
        style={{ marginTop: '6px' }}
      >
        {uploading ? t('uploading') : t('submitHomework')}
      </button>
      {uploadError && (
        <p style={{ color: 'var(--accent-red)', fontSize: '12px', marginTop: '6px' }}>{uploadError}</p>
      )}
      {uploadSuccess && (
        <p style={{ color: 'var(--accent-green)', fontSize: '12px', marginTop: '6px' }}>{t('uploadSuccess')}</p>
      )}
    </div>
  )
}
